import {FC, useEffect, useMemo, useState} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import * as Enums from "@/utils/enums"
import UserConfigService from "@/services/option/user-config-service";
import SCModal from "@/PageComponents/Modal/SCModal";
import {ColumnMappingData} from "@/PageComponents/Table/table-model";
import ScActiveColumns from "@/PageComponents/Table/Table Columns/ScActiveColumns";
import {Alert, Drawer, Flex, Title} from "@mantine/core";
import {SimpleColumnMapping} from "@/PageComponents/SimpleTable/SimpleTable";
import {useDebouncedValue, useDidUpdate} from "@mantine/hooks";
import {
    IconAdjustmentsStar,
} from "@tabler/icons-react";
import {IconCheckbox, IconInfoCircle} from "@tabler/icons";
import ScSwitch from "@/components/sc-controls/form-controls/sc-switch";
import hubspotService from "@/services/hubspot/hubspot-service";
import {useOverlayLayer} from "@/utils/atoms";

const COL_MAPPING_NAME = 'columns'
const LS_COL_MAPPING_NAME = 'quote-doc'

const getLsColumnMapping: (tableName: string) => ColumnMappingData[] | undefined = (tableName: any) => {
    try {
        const ls = localStorage.getItem(tableName + '-columns-')
        return ls && JSON.parse(ls) || undefined
    } catch (e) {
        return undefined
    }
}

const setLsColumnMapping = (tableName: string, columnMapping: ColumnMappingData[]) => {
    localStorage && localStorage.setItem(tableName + '-columns-', JSON.stringify(columnMapping))
}

const QuotePreferencesDrawer: FC<{
        open: boolean;
        onClose: () => void;
        onUserConfigLoaded?: (userAuthConfig: any) => void;
        onUserColumnConfigLoaded?: (columnMapping: ColumnMappingData[]) => void;
        columnMappingModelName?: string;
        mapping: SimpleColumnMapping[]
    }> = ({open, onClose, ...props}: any) => {

    const chatSafeAreaBottom = hubspotService.useChatSafeAreaBottom()
    useOverlayLayer(open) // registers as an open overlay layer so the parent QuoteDrawer knows not to close itself on an outside click meant for this drawer

    const [userConfig, setUserConfig] = useState<any>()
    const [userConfigModified, setUserConfigModified] = useState(false)
    const [metaDataColumnMapping, setMetaDataColumnMapping] = useState<ColumnMappingData[]>(
        getLsColumnMapping(LS_COL_MAPPING_NAME) ?? []
    )
    useEffect(() => {
        if (metaDataColumnMapping) {
            setLsColumnMapping(LS_COL_MAPPING_NAME, metaDataColumnMapping)
            props.onUserColumnConfigLoaded && props.onUserColumnConfigLoaded(metaDataColumnMapping)
        }
    }, [metaDataColumnMapping]);

    const usingInclusivePrice = useMemo(() =>
            metaDataColumnMapping.some((x: any) => x.ColumnName === 'UnitPriceInclusive' && x.Show === true),
        [metaDataColumnMapping]
    )

    const handleUsingInclusivePriceChange = (useInclusivePrice: any) => {
        const newColumnMapping = metaDataColumnMapping.map(
            (x: any) => (
                x.ColumnName === 'UnitPriceInclusive' ? {
                        ...x,
                        Show: useInclusivePrice,
                    } :
                    x.ColumnName === 'UnitPriceExclusive' ? {
                            ...x,
                            Show: !useInclusivePrice,
                        } :
                        /*x.ColumnName === 'LineTotalInclusive' ? {
                                ...x,
                                Show: useInclusivePrice,
                            } :
                            x.ColumnName === 'LineTotalExclusive' ? {
                                    ...x,
                                    Show: !useInclusivePrice,
                                } :*/
                                x
            )
        )

        handleColumnMappingChange(newColumnMapping, {key: useInclusivePrice ? 'UnitPriceInclusive' : 'UnitPriceExclusive', checked: true})
    }

    const handleNewReceivedColumnMapping = (newColumnMapping: ColumnMappingData[] | null) => {
        const columnMapping = /*newColumnMapping || */props.mapping.map((x: any, i: any) => {
            const colMappingData = newColumnMapping?.find((y: any) => y.ID === x.key)
            return (
                colMappingData ? {
                        ...colMappingData,
                        Sortable: !!x.columConfigOptions?.allowReorder, // ensures stored props can be overwritten safely if local config changes
                        IsRequired: !x.columConfigOptions?.allowShowToggle, // ensures stored props can be overwritten safely if local config changes
                        Label: x.label as string || colMappingData.Label,
                        Order: i, // force order as reordering is disabled on quote sections
                        Disabled: x.columConfigOptions?.disabled,
                        Show: x.columConfigOptions?.defaultShown === true && (!x.columConfigOptions?.allowShowToggle || x.columConfigOptions?.disabled) ? true : colMappingData.Show,
                    } :
                    {
                        Show: x.columConfigOptions?.defaultShown ?? true,
                        Sortable: !!x.columConfigOptions?.allowReorder,
                        ID: x.key,
                        Label: typeof x.label === 'string' ? x.label : '!!invalid-label-type!!',
                        ColumnName: x.key,
                        IsRequired: !x.columConfigOptions?.allowShowToggle,
                        Disabled: x.columConfigOptions?.disabled,
                        Order: i
                    })
        })
        setMetaDataColumnMapping(columnMapping)
    }

   useQuery(['authConfig', Enums.ConfigurationSection.Quote], () => UserConfigService.getSettings(Enums.ConfigurationSection.Quote, Enums.ConfigurationType.CRUD),
        {
        onSuccess: (userConfig: any) => {
            props.onUserConfigLoaded && props.onUserConfigLoaded(userConfig)
            setUserConfig(userConfig)

            handleNewReceivedColumnMapping(UserConfigService.getMetaDataValue(userConfig, COL_MAPPING_NAME))
        },
        onError: console.error
    })

    const authConfigMutation = useMutation(['quoteDocAutConfig'], UserConfigService.saveConfig)

    const [debouncedUserConfig] = useDebouncedValue(userConfig, 600)

    useDidUpdate(() => {
        if (userConfigModified) {
            setUserConfigModified(false)
            debouncedUserConfig && authConfigMutation.mutate(debouncedUserConfig)
        }
    }, [debouncedUserConfig])

    const handleAuthConfigChange = (newConfig: any) => {
        setUserConfig(newConfig)
        props.onUserConfigLoaded && props.onUserConfigLoaded(newConfig)
        setUserConfigModified(true)
    }

    const handleColumnMappingChange = (cm: ColumnMappingData[], itemChanged?: {key: string, checked: boolean}) => {
        let newColMapping = [...cm]
        if(
            (itemChanged?.key === 'UnitCostPrice' || itemChanged?.key === 'UnitPriceExclusive' || itemChanged?.key === 'UnitPriceInclusive') && !itemChanged?.checked
        ) {
            newColMapping = newColMapping.map((x: any) =>
                (x.ColumnName === 'UnitPriceMarkup') && x.Show ? {...x, Show: false} : x
            )
        } else if (itemChanged?.key === 'UnitPriceMarkup' && itemChanged?.checked) {
            const inclusiveShown = newColMapping.find((x: any) => x.ColumnName === 'UnitPriceInclusive')?.Show
            newColMapping = newColMapping.map((x: any) =>
                (x.ColumnName === 'UnitCostPrice') ? {...x, Show: true} :
                    (x.ColumnName === 'UnitPriceExclusive') ? {...x, Show: !inclusiveShown} :
                    (x.ColumnName === 'UnitPriceInclusive') ? {...x, Show: inclusiveShown} : x
            )
        }
        
        /*if(itemChanged?.key === 'UnitPriceExclusive' || itemChanged?.key === 'UnitPriceInclusive' || itemChanged?.key === 'UnitPriceMarkup') {
            const exclusiveShown = newColMapping.find(x => x.ColumnName === 'UnitPriceExclusive')?.Show
            const inclusiveShown = newColMapping.find(x => x.ColumnName === 'UnitPriceInclusive')?.Show
            newColMapping = newColMapping.map(x =>
                (x.ColumnName === 'UnitPriceExclusive') ? {
                    ...x,
                    Disabled: inclusiveShown,
                } : x.ColumnName === 'UnitPriceInclusive' ? {
                    ...x,
                    Disabled: exclusiveShown,
                } : x
            );
        }*/

        setMetaDataColumnMapping(newColMapping)
        if(userConfig) {
            props.onUserColumnConfigLoaded && props.onUserColumnConfigLoaded(newColMapping)
            handleAuthConfigChange({
                ...userConfig,
                MetaData: JSON.stringify({
                    ...(JSON.parse(userConfig.MetaData)),
                    [COL_MAPPING_NAME]: newColMapping
                })
            })
        } else {
            console.error('user config not loaded')
        }
    }


    return <>
        <Drawer
            opened={open}
            onClose={onClose}
            position={'right'}
            title={
                <Title order={4} c={'scBlue.9'}>
                    <Flex align={'center'} gap={5}>
                        <IconAdjustmentsStar size={20}/>
                        My Quote Preferences
                    </Flex>
                </Title>
            }
            overlayProps={{
                color: 'var(--mantine-color-scBlue-5)',
                blur: 10,
                opacity: .15
            }}
            // size={'xs'}
            styles={{ body: { paddingBottom: chatSafeAreaBottom } }}
        >

            {
            }
            {
                metaDataColumnMapping && <>
                    <Title order={5} c={'scBlue.9'}>
                        <Flex align={'center'} gap={5}>
                            <IconCheckbox size={20}/>
                            Displayed Columns
                        </Flex>
                    </Title>
                    <ScActiveColumns
                        columnMapping={metaDataColumnMapping}
                        onChange={handleColumnMappingChange}
                        hideRequired
                    />

                <ScSwitch
                    label={'Use Inclusive Pricing'}
                    checked={usingInclusivePrice}
                    onToggle={(checked: any) => handleUsingInclusivePriceChange(checked)}
                />
                </>
            }
            <Alert
                mt={'md'}
                variant={'light'}
                color={'teal'}
                icon={<IconInfoCircle />}
            >
                Updates won&apos;t affect the Quote PDF.
            </Alert>
        </Drawer>
    </>
}

export default QuotePreferencesDrawer
